/** /activities — cross-tenant Activity catalog (grid).
 *
 * Columns: name → RouterLink, type chip, category, price (price_type_display),
 * country; hidden until asked for, the record work's Display name, Custom ID, Images
 * and Source (the source in the shared catalog words, as on the record's header) — none
 * of them sortable. Filters: tenant (one or several), a `type` select, and a category
 * picker scoped to the selected tenant (exactly one). The type chip and the type
 * options read the one shared `ACTIVITY_TYPE_LABELS` ("Attraction", as on the
 * record). Changing the tenant also clears the category in ONE URL write — two
 * `setParam`s in a row each start from the same render's URL, so the second undid
 * the first. */
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Chip, CircularProgress, MenuItem, Stack, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import * as activitiesApi from '@/api/activities';
import { ACTIVITY_TYPE_LABELS, type ActivityRow } from '@/api/activities';
import * as categoriesApi from '@/api/activityCategories';
import type { ActivityCategoryRow } from '@/api/activityCategories';
import { deleteBatchAction } from '@/components/DataGrid/batchTypes';
import { batchEditAction } from '@/features/batch-edit/batchEditAction';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import {
  QuerySearchInput,
  parse,
  serialize,
  toWireAst,
  type FilterNode,
} from '@/components/QuerySearch';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { useSnackbar } from '@/components/Snackbar';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { AiImportEntryButton } from '@/features/ai-import/AiImportEntryButton';
import { CatalogSourceCell } from '@/features/catalog/catalogCells';
import { catalogSourceName } from '@/features/catalog/words';
import { entityExportBatchAction } from '@/features/exports/EntityExportDialog';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { formatRelativeTime } from '@/utils/format';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, singleTenantId, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';

import { buildActivityNamespaces, PAYMENT_TYPE_LABELS } from './activitiesNamespaces';
import { ActivityCreateDialog } from './ActivityCreateDialog';
import { activityTypeLabel } from './record/parts/activityNames';

const digits = (v: string) => (/^\d+$/.test(v) ? Number(v) : undefined);

/** The list sends `payment_type` alone: worded as the detail's `payment_type_display` and
 *  the search's `payment_type` values (`PAYMENT_TYPE_LABELS`, in value order). */
const paymentTypeLabel = (n: number | null): string | null =>
  n == null ? null : (PAYMENT_TYPE_LABELS[n] ?? null);

const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
  { param: 'type', toApi: digits },
  { param: 'category', toApi: digits },
];
/** Presets save/restore the `?q=` DSL alongside the tenant/type/category params. */
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS, ['q']);
const DEFAULT_ORDER = [
  'id',
  'tenant',
  'name',
  'display_name',
  'custom_id',
  'type',
  'category',
  'price_type',
  'country_name',
  'cost_min',
  'cost_max',
  'currency',
  'cost_prefix',
  'cost_unit',
  'payment_type',
  'price_level',
  'address',
  'image_count',
  'source_name',
  'updated_ts',
];

/** Columns present but hidden until toggled on via the column menu. */
const DEFAULT_HIDDEN = [
  'display_name',
  'custom_id',
  'cost_prefix',
  'cost_unit',
  'payment_type',
  'price_level',
  'address',
  'image_count',
  'source_name',
];

export function ActivitiesListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_activities_manage');
  const [createOpen, setCreateOpen] = useState(false);
  useCreateIntent(() => setCreateOpen(true));
  const snackbar = useSnackbar();
  const [sp, setSp] = useSearchParams();

  const presets = useGridPresets({
    grid: 'activities',
    defaultOrder: DEFAULT_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: 'name',
    filters: PRESET_FILTERS,
  });

  const {
    extra,
    getParam,
    setParam,
    setParams,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  // The operators the effective tenant filter names (URL > preset > scope): the
  // picker's value, and what the create dialog pre-selects / features (D9).
  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());
  // The single-tenant extras (category picker and suggestions, batch, AI import)
  // act only when the filter names exactly one operator (D5).
  const tenantId = singleTenantId(pageTenants.map((t) => t.id));
  const typeParam = getParam('type') ?? 'all';
  const categoryParam = getParam('category');
  // The same rule as the API side (`digits`): a hand-edited non-id is no filter.
  const categoryId = categoryParam && /^\d+$/.test(categoryParam) ? Number(categoryParam) : null;

  // ── QuerySearch (`?q=` DSL → wire AST) ─────────────────────────────────────
  const namespaces = useMemo(() => buildActivityNamespaces(), []);
  const q = presets.getParam('q') ?? '';
  const { tree, diagnostics } = useMemo(() => {
    const r = parse(q, namespaces);
    return { tree: r.nodes, diagnostics: r.diagnostics };
  }, [q, namespaces]);
  const wire = useMemo(() => toWireAst(tree), [tree]);

  const { beforeUrlWrite } = presets;
  const setQuery = useCallback(
    (nodes: FilterNode[]) => {
      const next = new URLSearchParams(sp);
      beforeUrlWrite(next); // divergence choke point (materializes a loaded preset)
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
    },
    [sp, setSp, beforeUrlWrite],
  );

  const lastDiagKey = useRef<string>('');
  useEffect(() => {
    if (diagnostics.length === 0) return;
    const key = `${q}::${diagnostics.length}`;
    if (key === lastDiagKey.current) return;
    lastDiagKey.current = key;
    snackbar.show(
      `Some of your search couldn't be interpreted: ${diagnostics[0]!.message}`,
      'warning',
    );
  }, [diagnostics, q, snackbar]);

  // The wire AST rides in `extra.filters` (a JSON string): `extra` is the only
  // channel ResourceDataGrid folds into BOTH the react-query key and the batch
  // `filterQuery`, so a query change refetches the list AND "select all matching"
  // applies to the filtered set — with zero ResourceDataGrid change.
  const gridExtra = useMemo(
    () => ({ ...extra, ...(wire.length ? { filters: JSON.stringify(wire) } : {}) }),
    [extra, wire],
  );
  const extraFilterValues = tenantId !== null ? { tour_operator_id: String(tenantId) } : undefined;

  const columns = useMemo<GridColDef<ActivityRow>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        sortable: false,
        valueGetter: (_, row) => row.tenant?.name ?? '',
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} />,
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
        renderCell: (p) => (
          <RouterLink to={`/activities/${p.row.id}`} style={{ color: '#0a1a3f', fontWeight: 500 }}>
            {p.row.name}
          </RouterLink>
        ),
      },
      // The record work's columns (plan §10.4), hidden by default. None is orderable: the
      // backend's ordering whitelist leaves them out, and `image_count` / `source_name` are
      // read for the page's rows only (Appendix A 3g).
      {
        field: 'display_name',
        headerName: 'Display name',
        width: 200,
        sortable: false,
        valueGetter: (_, row) => row.display_name ?? '',
        renderCell: (p) => p.row.display_name || '—',
      },
      {
        field: 'custom_id',
        headerName: 'Custom ID',
        width: 140,
        sortable: false,
        valueGetter: (_, row) => row.custom_id ?? '',
        renderCell: (p) => p.row.custom_id || '—',
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 140,
        renderCell: (p) => (
          <Chip size="small" variant="outlined" label={activityTypeLabel(p.row)} />
        ),
      },
      {
        field: 'category',
        headerName: 'Category',
        width: 170,
        sortable: false,
        valueGetter: (_, row) => row.category?.title ?? '',
        renderCell: (p) =>
          p.row.category ? (
            <RouterLink
              to={`/activity-categories/${p.row.category.id}`}
              style={{ color: '#0a1a3f' }}
              onClick={(e) => e.stopPropagation()}
            >
              {p.row.category.title}
            </RouterLink>
          ) : (
            '—'
          ),
      },
      {
        field: 'price_type',
        headerName: 'Price',
        width: 130,
        sortable: false,
        valueGetter: (_, row) => row.price_type_display ?? '',
        renderCell: (p) => p.row.price_type_display || '—',
      },
      {
        field: 'country_name',
        headerName: 'Country',
        width: 150,
        sortable: false,
        valueGetter: (_, row) => row.country_name ?? '',
        renderCell: (p) => p.row.country_name || '—',
      },
      {
        field: 'cost_min',
        headerName: 'Cost min',
        width: 110,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_, row) => row.cost_min,
        renderCell: (p) => (p.row.cost_min != null ? p.row.cost_min : '—'),
      },
      {
        field: 'cost_max',
        headerName: 'Cost max',
        width: 110,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_, row) => row.cost_max,
        renderCell: (p) => (p.row.cost_max != null ? p.row.cost_max : '—'),
      },
      {
        field: 'currency',
        headerName: 'Currency',
        width: 100,
        sortable: false,
        valueGetter: (_, row) => row.currency?.code ?? '',
        renderCell: (p) => p.row.currency?.code || '—',
      },
      {
        field: 'cost_prefix',
        headerName: 'Cost prefix',
        width: 120,
        sortable: false,
        valueGetter: (_, row) => row.cost_prefix ?? '',
        renderCell: (p) => p.row.cost_prefix || '—',
      },
      {
        field: 'cost_unit',
        headerName: 'Cost unit',
        width: 120,
        sortable: false,
        valueGetter: (_, row) => row.cost_unit ?? '',
        renderCell: (p) => p.row.cost_unit || '—',
      },
      {
        field: 'payment_type',
        headerName: 'Payment type',
        width: 150,
        sortable: false,
        valueGetter: (_, row) => paymentTypeLabel(row.payment_type) ?? '',
        renderCell: (p) => paymentTypeLabel(p.row.payment_type) ?? '—',
      },
      {
        field: 'price_level',
        headerName: 'Price level',
        width: 110,
        sortable: false,
        valueGetter: (_, row) => row.price_level,
        renderCell: (p) => (p.row.price_level != null ? p.row.price_level : '—'),
      },
      {
        field: 'address',
        headerName: 'Address',
        flex: 1,
        minWidth: 200,
        sortable: false,
        valueGetter: (_, row) => row.address ?? '',
        renderCell: (p) => p.row.address || '—',
      },
      {
        field: 'image_count',
        headerName: 'Images',
        width: 90,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        description: 'Images in its gallery.',
        valueGetter: (_, row) => row.image_count,
      },
      {
        field: 'source_name',
        headerName: 'Source',
        width: 170,
        sortable: false,
        description: 'The import source of the newest import that touched it.',
        valueGetter: (_, row) => catalogSourceName(row.source_name),
        renderCell: (p) => <CatalogSourceCell name={p.row.source_name} />,
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 130,
        // updated_ts IS in the backend _ORDERABLE whitelist — sortable.
        valueGetter: (_, row) => row.updated_ts,
        renderCell: (p) => (
          <span title={p.row.updated_ts ?? ''}>{formatRelativeTime(p.row.updated_ts)}</span>
        ),
      },
    ],
    [],
  );

  const orderingFallback = presets.ordering;
  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      activitiesApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? orderingFallback ?? 'name',
        // extra now carries tour_operator_id / type / category / filters (the wire AST).
        ...(params.extra as Record<string, never>),
        ...(params.timeoutTier ? { timeout: params.timeoutTier } : {}),
      }),
    [orderingFallback],
  );

  const anyActive = anyFilterActive || q.trim().length > 0;
  const onResetAll = useCallback(() => {
    resetAllFilters(); // clears tenant / type / category
    setQuery([]); // clears the rich query (?q=)
  }, [resetAllFilters, setQuery]);

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant',
      priority: 5,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            // One write: the category belongs to the tenant it was picked under.
            onChange={(next) =>
              setParams({ tenant: formatTenantIds(next.map((t) => t.id)), category: null })
            }
          />
        </Box>
      ),
    },
    {
      key: 'type',
      priority: 4,
      active: typeParam !== 'all',
      node: (
        <TextField
          select
          size="small"
          label="Type"
          value={typeParam}
          sx={{ width: 170 }}
          onChange={(e) => setParam('type', e.target.value === 'all' ? null : e.target.value)}
        >
          <MenuItem value="all">All types</MenuItem>
          {Object.entries(ACTIVITY_TYPE_LABELS).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      key: 'category',
      priority: 3,
      active: categoryId !== null,
      node: (
        <Box sx={{ minWidth: 240 }}>
          <CategoryFilter
            tenantId={tenantId}
            categoryId={categoryId}
            onChange={(id) => setParam('category', id != null ? String(id) : null)}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="activities"
        title="Activities"
        subtitle="Cross-tenant activity catalog."
        actions={
          <>
            <AiImportEntryButton type="activity" tenantId={tenantId} />
            {canManage && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
              >
                New activity
              </Button>
            )}
          </>
        }
      />

      <ActivityCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        pageTenants={pageTenants}
      />

      <Stack sx={{ mb: 2 }}>
        <QuerySearchInput
          namespaces={namespaces}
          value={tree}
          onChange={setQuery}
          {...(extraFilterValues ? { extraFilterValues } : {})}
          placeholder="Search activities…  e.g. type=Attraction & cost=..50 & country=France"
        />
      </Stack>

      <ResponsiveFilterBar items={toolbarItems} />

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<ActivityRow>
          queryKey={['activities']}
          queryFn={queryFn}
          allowExtendedTimeout
          columns={orderColumns(columns, presets.orderedFields)}
          extra={gridExtra}
          batchResource="activities"
          batchTenantFilterId={tenantId}
          batchActions={[
            entityExportBatchAction({
              permission: 'console_activities_export',
              entity: 'activity',
              resource: 'activities',
              noun: 'activity(s)',
            }),
            deleteBatchAction('console_activities_manage'),
            batchEditAction('activities', 'activity'),
          ]}
          emptyMessage="No activities yet."
          emptyAction={
            canManage && (
              <Button size="small" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                New activity
              </Button>
            )
          }
          defaultSort={{ field: 'name', sort: 'asc' }}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          headerToolbar={{
            onResetAllFilters: onResetAll,
            resetDisabled: !anyActive,
            exportFileName: 'activities',
            presets: presets.toolbar,
          }}
        />
      )}
    </Box>
  );
}

/** What the category picker shows: the picked row, or one looked up by id. */
type CategoryOption = Pick<ActivityCategoryRow, 'id' | 'title'>;

/** Category filter — a category picker scoped to the selected tenant (empty
 *  options, and a "pick one tenant" hint, unless exactly one tenant is chosen).
 *  The URL param is the value. A category picked here shows the picked option;
 *  one that arrived in the URL (a count link, a shared view, a preset) is looked
 *  up by id, so an active filter is always shown — never applied behind an empty
 *  picker. Until the lookup answers (or when the category is gone) it reads
 *  "Category #<id>". */
function CategoryFilter({
  tenantId,
  categoryId,
  onChange,
}: {
  tenantId: number | null;
  categoryId: number | null;
  onChange: (id: number | null) => void;
}) {
  const [picked, setPicked] = useState<CategoryOption | null>(null);
  const known = picked !== null && picked.id === categoryId;
  // The detail page's key and shape: a visit there answers this lookup too.
  const lookup = useQuery({
    queryKey: ['activity_categories', 'detail', categoryId],
    queryFn: () => categoriesApi.retrieve(categoryId!),
    enabled: categoryId !== null && !known,
  });
  let shown: CategoryOption | null = null;
  if (categoryId !== null) {
    shown = known ? picked : (lookup.data ?? { id: categoryId, title: `Category #${categoryId}` });
  }
  return (
    <RemoteAutocomplete<CategoryOption>
      label="Category"
      size="small"
      value={shown}
      getOptionLabel={(o) => o.title}
      getOptionKey={(o) => o.id}
      fetchOnEmpty={tenantId !== null}
      noOptionsText={tenantId === null ? 'Pick one tenant to filter by category' : undefined}
      queryFn={async (q) =>
        tenantId != null
          ? (
              await categoriesApi.list({
                tour_operator_id: tenantId,
                search: q || undefined,
                pageSize: 50,
              })
            ).results
          : []
      }
      onChange={(v) => {
        const c = (v as CategoryOption | null) ?? null;
        setPicked(c);
        onChange(c?.id ?? null);
      }}
    />
  );
}
